import type { KeyboardEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getCountryFlagUrl } from "assets/icons/flagUrls";
import cn from "classnames";
import { COUNTRIES_LIST } from "constants/countries";
import { SITE_NAME } from "constants/siteName";
import { useSiteName } from "context/SiteName.context";
import { Input } from "../Input/Input";
import styles from "./PhoneSelectList.module.scss";

type Country = {
  name: string;
  iso: string;
  countryCode: string;
};

type PhoneSelectListProps = {
  onCountryCodeChange: (country: Country) => void;
};

const normalizeText = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const digitsOnly = (value: string): string => value.replace(/\D/g, "");

const countryMatchesQuery = (country: Country, query: string): boolean => {
  const rawQuery = query.trim();
  if (!rawQuery) {
    return true;
  }

  const textQuery = normalizeText(rawQuery);
  const name = normalizeText(country.name);
  const dialCode = normalizeText(country.iso);
  const countryCode = normalizeText(country.countryCode);

  if (name.includes(textQuery) || dialCode.includes(textQuery) || countryCode.includes(textQuery)) {
    return true;
  }

  const queryDigits = digitsOnly(rawQuery);
  const dialDigits = digitsOnly(country.iso);

  return Boolean(queryDigits && dialDigits.includes(queryDigits));
};

export const PhoneSelectList = ({ onCountryCodeChange }: PhoneSelectListProps) => {
  const { siteName } = useSiteName();
  const isChineseBrothers = siteName === SITE_NAME.CHINESE_BROTHERS;
  const listRef = useRef<HTMLUListElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");

  const filteredCountries = useMemo(
    () => COUNTRIES_LIST.filter((country) => countryMatchesQuery(country, query)),
    [query]
  );

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();

    const firstMatch = filteredCountries[0];
    if (firstMatch) {
      onCountryCodeChange(firstMatch);
    }
  };

  return (
    <div
      className={cn(styles.list, {
        [styles.listChineseBrothers]: isChineseBrothers,
      })}
    >
      <div className={styles.search}>
        <Input
          ref={searchInputRef}
          type="text"
          autoComplete="off"
          value={query}
          className={styles.searchInput}
          placeholder="Search country or code"
          aria-label="Search country or code"
          onChange={(event) => setQuery(event.currentTarget.value)}
          onKeyDown={handleSearchKeyDown}
          onClick={(event) => event.stopPropagation()}
        />
      </div>
      <ul ref={listRef} className={styles.items}>
        {filteredCountries.length > 0 ? (
          filteredCountries.map((country) => {
            const flagSrc = getCountryFlagUrl(country.countryCode);
            return (
              <li
                key={`${country.countryCode}_${country.name}`}
                onClick={() => onCountryCodeChange(country)}
                className={styles.item}
              >
                <img className={styles.itemFlag} src={flagSrc} alt="" />
                <span className={styles.itemName}>
                  {country.name} {country.iso}
                </span>
              </li>
            );
          })
        ) : (
          <li className={styles.empty}>No countries found</li>
        )}
      </ul>
    </div>
  );
};
